import React, { useState } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface HideContentProps {
  content: string;
  postId: string;
  userId?: string;
  requireLogin?: boolean;
  requireReply?: boolean;
  onReveal?: (postId: string, blockId: string) => void;
  renderContent?: (content: string) => React.ReactNode;
}


const HideContent: React.FC<HideContentProps> = ({
  content,
  postId,
  userId,
  requireLogin = false,
  requireReply = false,
  onReveal,
  renderContent
}) => {
  const [revealedBlocks, setRevealedBlocks] = useState<Set<string>>(new Set());

  const handleRevealClick = async (blockId: string) => {
    if (requireLogin && !userId) {
      alert('请先登录后查看隐藏内容');
      return;
    }

    if (requireReply) {
      alert('请先回复后查看隐藏内容');
      return;
    }

    // 触发回调（如果需要记录用户行为）
    if (onReveal) {
      onReveal(postId, blockId);
    }

    // 本地状态更新
    setRevealedBlocks(prev => new Set([...prev, blockId]));
  };

  // 渲染最终内容
  const renderFinalContent = () => {
    // 将内容分段处理，支持隐藏块
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let partIndex = 0;

    // 查找所有隐藏块的位置
    const hideRegex = /\[hide\](.*?)\[\/hide\]/gs;
    let match;

    while ((match = hideRegex.exec(content)) !== null) {
      const start = match.index;
      const end = hideRegex.lastIndex;
      
      // 添加隐藏块之前的普通内容
      if (start > lastIndex) {
        const normalContent = content.substring(lastIndex, start);
        if (normalContent.trim()) {
          parts.push(
            <div key={`normal-${partIndex++}`}>
              {renderContent ? renderContent(normalContent) : (
                <div dangerouslySetInnerHTML={{ __html: normalContent }} />
              )}
            </div>
          );
        }
      }
      
      // 添加隐藏块
      const blockId = `hide-${postId}-${parts.length}`;
      const hiddenContent = match[1].trim();
      const isRevealed = revealedBlocks.has(blockId);
      
      if (isRevealed) {
        parts.push(
          <div
            key={blockId}
            className="hide-content-revealed bg-blue-50 border-2 border-blue-200 rounded-lg p-4 my-4"
          >
            <div className="flex items-center mb-3 text-blue-700 font-medium text-sm">
              <Unlock className="h-4 w-4 mr-2 text-green-500" />
              隐藏内容已解锁
            </div>
            <div className="revealed-content">
              {renderContent ? renderContent(hiddenContent) : (
                <div dangerouslySetInnerHTML={{ __html: hiddenContent }} />
              )}
            </div>
          </div>
        );
      } else {
        parts.push(
          <div
            key={blockId}
            className="hide-content-preview bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-lg p-6 my-4 text-center cursor-pointer hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 hover:scale-105 hover:shadow-lg"
            onClick={() => handleRevealClick(blockId)}
          >
            <Lock className="h-8 w-8 mx-auto mb-3 opacity-90" />
            <div className="font-semibold text-lg mb-2">隐藏内容</div>
            <div className="text-sm opacity-80 mb-4">
              {requireLogin && !userId && '请先登录后查看'}
              {requireReply && '回复后可见'}
              {!requireLogin && !requireReply && '点击查看'}
            </div>
            {!requireLogin && !requireReply && (
              <Button 
                size="sm" 
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                点击解锁
              </Button>
            )}
          </div>
        );
      }
      
      lastIndex = end;
    }
    
    // 添加最后剩余的普通内容
    if (lastIndex < content.length) {
      const remainingContent = content.substring(lastIndex);
      if (remainingContent.trim()) {
        parts.push(
          <div key={`normal-final`}>
            {renderContent ? renderContent(remainingContent) : (
              <div dangerouslySetInnerHTML={{ __html: remainingContent }} />
            )}
          </div>
        );
      }
    }

    return (
      <div className="hide-content-container">
        {parts}
      </div>
    );
  };

  return (
    <div className="hide-content-wrapper">
      {renderFinalContent()}
      
      <style jsx global>{`
        .hide-content-revealed {
          animation: revealAnimation 0.5s ease-out;
        }

        @keyframes revealAnimation {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .hide-content-preview {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .hide-content-preview:hover {
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }
      `}</style>
    </div>
  );
};

export default HideContent;